import { atom } from 'jotai';
import {
  ChatThreadManager,
  ChatThreadScope,
  EditorContext,
  normalizeThreadMessages,
} from '../ChatThreadManager';
import { UIMessage } from 'ai';

// Type for thread metadata
export type ThreadMetadata = {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
  messageCount?: number;
  userMessageCount?: number;
  topicId?: string;
  docId?: string;
  editorContext?: EditorContext;
  archivedAt?: string | null;
  workspaceId?: string;
  scopeType?: string;
  scopeId?: string;
  scopeLabel?: string;
  scopePath?: string;
  lastAskAiAt?: string;
};

// Import ChatThread type from ChatThreadManager
export type { ChatThread, EditorContext, DocumentContext } from '../ChatThreadManager';

// Atom for the list of thread metadata
export const chatThreadsAtom = atom<ThreadMetadata[]>([]);

export function isUnhelpfulThreadName(name: string | null | undefined): boolean {
  const normalized = (name ?? '').trim().toLowerCase();
  return (
    normalized === '' ||
    normalized === 'new chat' ||
    normalized === 'screenata' ||
    normalized === 'page' ||
    normalized === 'page purpose summary' ||
    normalized.startsWith('user is viewing')
  );
}

export function isNormalScreenataChatThread(
  thread: Pick<
    ThreadMetadata,
    'id' | 'scopeType' | 'scopeId' | 'scopePath'
  >
): boolean {
  if (thread.scopeType || thread.scopeId || thread.scopePath) return false;

  const id = thread.id ?? '';
  return (
    !id.startsWith('screenata-policy-') &&
    !id.startsWith('screenata-onboarding-') &&
    !id.startsWith('session-')
  );
}

export function isDisplayableNormalChatThread(
  thread: Pick<
    ThreadMetadata,
    'id' | 'name' | 'scopeType' | 'scopeId' | 'scopePath' | 'userMessageCount'
  >
): boolean {
  if ((thread.userMessageCount ?? 0) <= 0) return false;
  if (!isNormalScreenataChatThread(thread)) return false;
  const name = (thread.name ?? '').trim().toLowerCase();
  if (name && name !== 'new chat' && isUnhelpfulThreadName(name)) return false;
  return true;
}

export function isDisplayableWorkspaceChatThread(
  thread: Pick<
    ThreadMetadata,
    | 'id'
    | 'name'
    | 'scopeType'
    | 'scopeId'
    | 'scopePath'
    | 'userMessageCount'
    | 'workspaceId'
    | 'lastAskAiAt'
  >,
  workspaceId: string | null | undefined
): boolean {
  const isDisplayableNormalThread = isDisplayableNormalChatThread(thread);
  const isDisplayableTestDetailThread =
    ((thread.userMessageCount ?? 0) > 0 || !!thread.lastAskAiAt) &&
    thread.scopeType === 'test-detail' &&
    !!thread.scopeId &&
    !isUnhelpfulThreadName(thread.name);

  if (!isDisplayableNormalThread && !isDisplayableTestDetailThread) {
    return false;
  }

  return isThreadInWorkspace(thread, workspaceId);
}

export function isThreadInWorkspace(
  thread: Pick<ThreadMetadata, 'workspaceId'>,
  workspaceId: string | null | undefined,
  options: { includeLegacyUnscoped?: boolean } = {}
): boolean {
  if (!workspaceId) return true;
  if (!thread.workspaceId) return options.includeLegacyUnscoped ?? false;
  return thread.workspaceId === workspaceId;
}

// Atom for the current selected thread id — persisted to localStorage
const CURRENT_THREAD_KEY = 'chat:currentThreadId';

const baseCurrentThreadIdAtom = atom<string | null>(
  typeof window !== 'undefined'
    ? localStorage.getItem(CURRENT_THREAD_KEY)
    : null
);

export const currentThreadIdAtom = atom(
  (get) => get(baseCurrentThreadIdAtom),
  (_get, set, value: string | null) => {
    set(baseCurrentThreadIdAtom, value);
    if (typeof window !== 'undefined') {
      if (value) {
        localStorage.setItem(CURRENT_THREAD_KEY, value);
      } else {
        localStorage.removeItem(CURRENT_THREAD_KEY);
      }
    }
  },
);

function countUserMessages(messages: UIMessage[] | undefined): number {
  return (messages ?? []).filter((message) => message.role === 'user').length;
}

// Async atom to load threads from localforage
export const loadThreadsAtom = atom(null, async (_get, set) => {
  try {
    const threads = await ChatThreadManager.getThreads();

    // Always update to ensure we have fresh data
    set(
      chatThreadsAtom,
      threads.map((thread) => ({
        ...thread,
        archivedAt: thread.archivedAt ?? null,
      }))
    );
  } catch (error) {
    console.error('Failed to load threads:', error);
    // Set empty array on error to prevent infinite loading
    set(chatThreadsAtom, []);
  }
});

// Async atom to create a new thread
export const createThreadAtom = atom(
  null,
  async (
    get,
    set,
    {
      name,
      topicId,
      docId,
      id,
      editorContext,
      scope,
      messages,
    }: {
      name?: string
      topicId?: string
      docId?: string
      id?: string
      editorContext?: EditorContext
      scope?: ChatThreadScope
      messages?: UIMessage[]
    }
  ) => {
    const thread = await ChatThreadManager.createThread(
      name,
      topicId,
      docId,
      id,
      editorContext,
      scope,
      messages
    );

    // Bug Fix: Don't reload - createThread already added to list
    // Just add metadata to local atom state
    const currentThreads = get(chatThreadsAtom);
    const newMetadata = {
      id: thread.id,
      name: thread.name,
      createdAt: thread.createdAt,
      updatedAt: thread.updatedAt,
      messageCount: thread.messages.length,
      userMessageCount: countUserMessages(thread.messages),
      topicId: thread.topicId,
      docId: thread.docId,
      editorContext: thread.editorContext,
      archivedAt: thread.archivedAt ?? null,
      workspaceId: thread.workspaceId,
      scopeType: thread.scopeType,
      scopeId: thread.scopeId,
      scopeLabel: thread.scopeLabel,
      scopePath: thread.scopePath,
      lastAskAiAt: thread.lastAskAiAt,
    };

    set(chatThreadsAtom, [...currentThreads, newMetadata]);
    set(currentThreadIdAtom, thread.id);

    return thread;
  }
);

// Async atom to create thread from editor context
export const createThreadFromEditorAtom = atom(
  null,
  async (get, set, context: {
    prompt: string;
    topicId?: string;
    topicTitle?: string;
    docId?: string;
    selection: { from: number; to: number };
    selectedText: string;
  }) => {
    const thread = await ChatThreadManager.createThreadFromEditorContext(context);

    // Add to beginning of threads list (most recent)
    const currentThreads = get(chatThreadsAtom);
    const threadMetadata = {
      id: thread.id,
      name: thread.name,
      createdAt: thread.createdAt,
      updatedAt: thread.updatedAt,
      messageCount: thread.messages.length,
      userMessageCount: countUserMessages(thread.messages),
      topicId: thread.topicId,
      docId: thread.docId,
      editorContext: thread.editorContext,
      archivedAt: thread.archivedAt ?? null,
      workspaceId: thread.workspaceId,
      scopeType: thread.scopeType,
      scopeId: thread.scopeId,
      scopeLabel: thread.scopeLabel,
      scopePath: thread.scopePath,
      lastAskAiAt: thread.lastAskAiAt,
    };

    set(chatThreadsAtom, [threadMetadata, ...currentThreads]);
    set(currentThreadIdAtom, thread.id);

    return thread;
  }
);

// Async atom to delete a thread
export const deleteThreadAtom = atom(null, async (get, set, threadId: string) => {
  await ChatThreadManager.deleteThread(threadId);
  const threads = get(chatThreadsAtom).filter((t) => t.id !== threadId);
  set(chatThreadsAtom, threads);
  if (get(currentThreadIdAtom) === threadId) {
    const newThreadId = threads.length > 0 ? threads[0].id : null;
    set(currentThreadIdAtom, newThreadId);
  }
});

export const syncThreadMessagesAtom = atom(
  null,
  async (
    _get,
    set,
    {
      threadId,
      messages,
      workspaceId,
    }: {
      threadId: string
      messages: UIMessage[]
      workspaceId?: string | null
    }
  ) => {
    // Get the actual thread data to compare messages
    try {
      const normalizedMessages = normalizeThreadMessages(messages);
      const existingThread = await ChatThreadManager.getThread(threadId);
      const userMessageCount = countUserMessages(normalizedMessages);
      let assignedWorkspaceId: string | null = null;

      if (
        workspaceId &&
        existingThread &&
        !existingThread.workspaceId &&
        userMessageCount > 0 &&
        isNormalScreenataChatThread(existingThread)
      ) {
        await ChatThreadManager.flushThread({
          ...existingThread,
          workspaceId,
        });
        assignedWorkspaceId = workspaceId;
      }

      // Compare message arrays to see if they've actually changed
      const messagesChanged =
        !existingThread?.messages ||
        existingThread.messages.length !== normalizedMessages.length ||
        JSON.stringify(existingThread.messages) !==
          JSON.stringify(normalizedMessages);

      // Only update if messages have actually changed
      if (messagesChanged) {
        await ChatThreadManager.setMessages(threadId, normalizedMessages);

        // Update chatThreadsAtom with the new updatedAt for immediate UI refresh
        set(chatThreadsAtom, (prevThreads) =>
          prevThreads.map((threadMeta) =>
            threadMeta.id === threadId
              ? {
                  ...threadMeta,
                  updatedAt: new Date().toISOString(),
                  messageCount: normalizedMessages.length,
                  userMessageCount,
                  workspaceId:
                    threadMeta.workspaceId ?? assignedWorkspaceId ?? undefined,
                }
              : threadMeta
          )
        );
      } else if (assignedWorkspaceId) {
        set(chatThreadsAtom, (prevThreads) =>
          prevThreads.map((threadMeta) =>
            threadMeta.id === threadId
              ? { ...threadMeta, workspaceId: assignedWorkspaceId }
              : threadMeta
          )
        );
      }
    } catch (error) {
      console.error('Failed to sync thread messages:', error);
    }
  }
);

// Touch a thread to bump its updatedAt — moves it to the top of the
// recents list without requiring a message. Used when the user selects
// a thread from sidebar Recents or from the history popover.
// Cross-route handoff: when the welcome page auto-creates a thread on
// first message, it stashes the user's text here and navigates to
// /chat/$threadId. The destination route reads this atom on mount and
// triggers sendMessage so the stream runs entirely within the new
// component instance. Cleared after consumption.
export const pendingUserMessageAtom = atom<{
  threadId: string
  text: string
  uploadedSources?: Array<{ id: string; name: string; url?: string; type?: string }>
} | null>(null)

export const openThreadHistoryAtom = atom(false)

export const touchThreadAtom = atom(null, async (_get, set, threadId: string) => {
  const now = new Date().toISOString();
  set(chatThreadsAtom, (prev) =>
    prev.map((t) => (t.id === threadId ? { ...t, updatedAt: now } : t))
  );
  try {
    const thread = await ChatThreadManager.getThread(threadId);
    if (thread) {
      thread.updatedAt = now;
      await ChatThreadManager.saveThread(thread);
    }
  } catch (error) {
    console.error('Failed to touch thread:', error);
  }
});

export const assignThreadWorkspaceAtom = atom(
  null,
  async (
    _get,
    set,
    {
      threadId,
      workspaceId,
    }: {
      threadId: string
      workspaceId: string
    }
  ) => {
    const thread = await ChatThreadManager.getThread(threadId);
    if (!thread || thread.workspaceId) return;
    if (countUserMessages(thread.messages) <= 0) return;
    if (!isNormalScreenataChatThread(thread)) return;

    await ChatThreadManager.flushThread({
      ...thread,
      workspaceId,
    });

    set(chatThreadsAtom, (prev) =>
      prev.map((t) => (t.id === threadId ? { ...t, workspaceId } : t))
    );
  }
);

// Async atom to archive / unarchive a thread
export const archiveThreadAtom = atom(
  null,
  async (_get, set, { threadId, archived }: { threadId: string; archived: boolean }) => {
    await ChatThreadManager.setThreadArchived(threadId, archived);
    const archivedAt = archived ? new Date().toISOString() : null;
    set(chatThreadsAtom, (prev) =>
      prev.map((t) =>
        t.id === threadId ? { ...t, archivedAt, updatedAt: new Date().toISOString() } : t
      )
    );
  }
);

// Async atom to update thread name
export const updateThreadNameAtom = atom(null, async (_get, set, { threadId, name }: { threadId: string, name: string }) => {
  console.log('>>> updateThreadNameAtom called:', { threadId, name });
  await ChatThreadManager.updateThreadName(threadId, name);
  
  // Update the thread metadata in the store
  set(chatThreadsAtom, (prev) => {
    const updated = prev.map(thread => 
      thread.id === threadId 
        ? { ...thread, name, updatedAt: new Date().toISOString() }
        : thread
    );
    // console.log('>>> updateThreadNameAtom: updated thread list', updated.find(t => t.id === threadId));
    return updated;
  });
  // console.log('>>> updateThreadNameAtom completed for:', threadId);
}); 
